import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Crosshair, Sprout, X, AlertTriangle, Check, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import FeedbackButtons, { SUGGESTION_REASONS } from "@/components/FeedbackButtons";
import UnifiedLeadCard from "@/components/UnifiedLeadCard";
import { HuntGuidancePanel } from "@/components/HuntGuidancePanel";
import { HuntSourceStatus } from "./HuntSourceStatus";

const TargetingSummary = ({ data }) => {
  const gates = data?.searchStats?.gates;
  const drops = data?.searchStats?.dropReasons || {};
  const rejected = (drops.below_min_size || 0) + (drops.above_max_size || 0) + (drops.industry_mismatch || 0);
  return (
    <>
      {data?.targeting && (
        <p className="text-[11px] text-gray-500 mt-0.5" data-testid="hunt-targeting-summary">
          Targeting: {(data.targeting.titles || []).slice(0, 3).join(", ") || "senior decision-makers"}
          {" · "}{(data.targeting.industries || []).slice(0, 3).join(", ")}
          {data.targeting.geo?.length ? ` · ${data.targeting.geo.slice(0, 2).join(", ")}` : ""}
          {data?.searchStats?.raw ? ` · ${data.searchStats.raw} results screened` : ""}
        </p>
      )}
      {gates && (gates.size || gates.industries?.length > 0) && (
        <p className="text-[11px] text-ism-teal/80 mt-0.5" data-testid="hunt-icp-gates">
          ICP gate: {gates.size ? `${gates.size} employees (±1 size bracket)` : "any size"}
          {" · "}{gates.industries?.length ? `${gates.industries.length} target industr${gates.industries.length === 1 ? "y" : "ies"} (strict)` : "any industry"}
          {rejected ? ` · ${rejected} firm${rejected === 1 ? "" : "s"} rejected for size/industry` : ""}
        </p>
      )}
      {data?.quota?.message && (
        <p className={`text-[11px] mt-1 flex items-center gap-1 ${data.quota.exceeded ? "text-red-300" : "text-amber-300"}`} data-testid="hunt-quota-banner">
          <AlertTriangle className="h-3 w-3 shrink-0" />{data.quota.message}
        </p>
      )}
      <HuntSourceStatus status={data?.searchStats?.sourceStatus} />
      {data?.searchStats?.market?.drawn > 0 && (
        <p className="text-[11px] text-gray-400 mt-1" data-testid="hunt-market-draw">
          Drew {data.searchStats.market.drawn} untouched firms from your Market map · one buyer per company
          {data.searchStats.market.growJobId ? " · map is growing in the background" : ""}
        </p>
      )}
    </>
  );
};

export const HuntResultsModal = ({ open, userId, results, savedLeads, dismissedLeads, dispositionOrder, savingLead, expandedLeadIndex, needsDisposition,
  onClose, onSave, onDismiss, onOutreach, onToggleExpand, onRerun }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  const isHunt = results?.mode?.toLowerCase() === "hunt";
  const found = results?.companiesFound || [];
  const remaining = found.filter((_, i) => !savedLeads.has(i) && !dismissedLeads.has(i)).length;
  return (
    <AnimatePresence>
      {open && results && found.length > 0 && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            className="glass-card relative z-10 w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl overflow-hidden border border-ism-teal/30"
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-5 border-b border-white/10 flex items-center justify-between gap-2 bg-gradient-to-r from-ism-teal/10 to-transparent flex-shrink-0">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <div className="p-2.5 rounded-xl bg-ism-teal/20 border border-ism-teal/30 flex-shrink-0">
                  {isHunt ? <Crosshair className="h-6 w-6 text-ism-teal" /> : <Sprout className="h-6 w-6 text-ism-teal" />}
                </div>
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1 min-w-0">
                  <h2 className="text-lg sm:text-xl text-white font-medium">{isHunt ? "Prospect Discovery" : "Account Expansion"}</h2>
                  <span className={`px-2 py-0.5 text-xs rounded-full uppercase tracking-wider ${isHunt ? "bg-green-500/20 text-green-400 border border-green-500/30" : "bg-blue-500/20 text-blue-400 border border-blue-500/30"}`}>
                    {isHunt ? "Hunt" : "Farm"}
                  </span>
                  <span className="text-sm text-gray-400">{found.length} found</span>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                <FeedbackButtons
                  userId={userId}
                  agentType={results.mode?.toLowerCase() || "hunt"}
                  actionId={results?.data?.actionId}
                  outputSummary={`${results.mode} generated ${found.length} results`}
                  size="small"
                  context={{ kind: "execution", mode: results?.mode?.toLowerCase() }}
                  reasons={SUGGESTION_REASONS}
                />
                <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors flex-shrink-0" aria-label="Close" data-testid="hunt-results-close-btn">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="px-5 py-3 border-b border-white/5 bg-white/5 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Prospects Found ({remaining} remaining)</p>
                  <TargetingSummary data={results.data} />
                </div>
                {needsDisposition && (
                  <span className="text-xs text-amber-400 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Disposition all leads to generate new ones
                  </span>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              <div className="space-y-4">
                {results?.data?.needsGuidance && results?.data?.guidance && (
                  <HuntGuidancePanel userId={userId} guidance={results.data.guidance} strongFound={found.length} onRerun={onRerun} />
                )}
                {remaining === 0 && (
                  <div className="glass-card-light rounded-xl p-6 text-center" data-testid="hunt-queue-complete">
                    <CheckCircle2 className="h-9 w-9 text-ism-teal mx-auto mb-2" />
                    <p className="text-white font-medium">Queue cleared — all {found.length} prospects dispositioned</p>
                    <p className="text-sm text-gray-400 mt-1">{savedLeads.size} saved to your CRM · {dismissedLeads.size} dismissed. Saved buyers are under Accounts; every decision is logged.</p>
                    {isHunt && onRerun && (
                      <Button onClick={onRerun} className="mt-4 bg-ism-teal hover:bg-ism-teal/90 text-black rounded-lg" data-testid="hunt-queue-next-btn">
                        <Crosshair className="h-4 w-4 mr-2" /> Hunt the next 8
                      </Button>
                    )}
                  </div>
                )}
                {found.map((company, idx) => (dismissedLeads.has(idx) || savedLeads.has(idx)) ? null : (
                  <UnifiedLeadCard
                    key={company.url || company.leadId || `${company.companyName}-${company.personName}-${idx}`}
                    lead={company}
                    index={idx}
                    userId={userId}
                    agentType={results?.mode?.toLowerCase() || "hunt"}
                    actionId={results?.data?.actionId}
                    onSave={onSave}
                    onDismiss={onDismiss}
                    onGenerateOutreach={onOutreach}
                    onViewDetails={() => onToggleExpand(idx)}
                    isSaved={savedLeads.has(idx)}
                    isSaving={savingLead === idx}
                    showActions={true}
                    showDismiss={true}
                    isExpanded={expandedLeadIndex === idx}
                  />
                ))}
              </div>
            </div>

            <div className="p-4 border-t border-white/10 bg-white/5 flex-shrink-0 space-y-3">
              {(savedLeads.size > 0 || dismissedLeads.size > 0) && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" data-testid="hunt-disposition-log">
                  <span className="text-gray-500 uppercase tracking-wider">Log</span>
                  {(dispositionOrder?.length ? dispositionOrder : [...savedLeads, ...dismissedLeads]).map((i) => found[i] && (savedLeads.has(i) ? (
                    <span key={`s${i}`} className="inline-flex items-center gap-1 text-ism-teal"><Check className="h-3 w-3" />{found[i].personName || found[i].companyName}<span className="text-gray-500">· saved</span></span>
                  ) : dismissedLeads.has(i) ? (
                    <span key={`d${i}`} className="inline-flex items-center gap-1 text-gray-400"><X className="h-3 w-3" />{found[i].personName || found[i].companyName}<span className="text-gray-600">· dismissed</span></span>
                  ) : null))}
                </div>
              )}
              <Button onClick={onClose} className="w-full bg-white/10 hover:bg-white/20 text-white">Close</Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
